export default {
  settings: {
    disableIframePostMessage: false,
  },

  client: 'CSC',
  lastUpdated: 'July 2026',
  revision: '3',
  analytics: {
    gtmcode:"GTM-UNDEFINED",
    gacode: ""
  },

  content: {
    title: "CSC | Retirement Lifestyle Target",
    // CSC uses SuperModeller v3 URL override parameter names.
    supermodellerVersion: "v3",
    supermodellerLifeStage: 1,
    supermodellerLink: "https://www.csc.gov.au/resources/tools/retirement-modeller",
    // supermodellerLinkADF: "https://www.csc.gov.au/calculators/modeller-adf/index.html",
    // supermodellerLinkPSSap: "https://www.csc.gov.au/members/resources/tools/pssap-retirement-modeller",
    welcome: {
      logoUrl: "CSC_Monogram_Artwork_RGB.svg",
      content: `
        <h2 class="lt-welcome-title" tabindex="0">Retirement Lifestyle Target</h2>
        <p>Retirement isn’t just about numbers — it’s about the lifestyle you’d like to live.</p>
        <p>This tool gives you a simple way to explore a retirement lifestyle target based on what matters most to you right now. There are no right or wrong answers, and you can come back and try it again at any time.</p>
        <p>Take a few minutes to see what your retirement lifestyle target looks like right now.</p>`,
      buttonLabel: "Let’s get started",
    },

    results: {
      header: "Your retirement lifestyle target",
      subheader: "Based on the lifestyle choices you’ve made, this gives you an estimate of the income you might need each year in retirement. You can go back and change your answers at any time.",
      supermodellerName: "retirement modeller",
      helpText: `The AFSA retirement standard calculates the annual expenditure in today’s dollars to maintain a basic, modest and comfortable lifestyle in retirement. The amount assumes that the retiree(s) own their own home outright and are relatively healthy. Learn more about the <a href='https://www.superannuation.asn.au/resources/retirement-standard' target='_blank' >ASFA Retirement standard and underlying assumptions</a>.`,
      CTA1: `
      <h3>Want to explore this a bit more?</h3>
      <p>See how long your super could last in retirement and different choices might change your income.</p>
      <a href="#" style="pointer-event:'none'" class="fake-button">Try the retirement modeller</a>
      `,
      CTA2: `
      <h3>Getting closer to retirement?</h3>
      <p>Join a Preparing for retirement webinar to understand your options and what to think about next.</p>
      <a href="https://www.csc.gov.au/Members/Advice-and-resources/Webinars" target="_blank">Check out upcoming webinars</a>
      `,
      CTA3: `
      <h3>Prefer to talk it through?</h3>
      <p>Book a  30 minute session with a CSC Super and Retirement Specialist (at no extra cost).</p>
      <a href="https://www.csc.gov.au/Members/Advice-and-resources/Financial-planning/Super-Specialists" target="_blank">Book today</a>
      `,
      CTA4: `
      <h3>Want to little more guidance?</h3>
      <p>Explore the Retire Ready modules and workbook to help you plan, prepare and live your best retirement</p>
      <a href="https://www.csc.gov.au/Members/Retirement/Plan-retirement/Retirement-Ready" target="_blank">Read them now</a>
      `,
    },

    standards: {
      minimum: {
        heading: "Age pension with a little bit extra",
        headingHousehold: "Household expenses",
        descriptionHousehold: "Covering the basics to keep the home running, with minimal repairs and maintenance.",
        headingTravel: "Travel",
        descriptionTravel: "Occasional short breaks or day trips close to home.",
        headingFood: "Food",
        descriptionFood: "Mostly low-cost food shopping, with meals out or take-away only now and then.",
        headingClothes: "Clothes",
        descriptionClothes: "Low-maintenance spending, covering essentials only."
      },
      modest: {
        heading: "Modest",
        headingHousehold: "Household expenses",
        descriptionHousehold: "Keeping the home well maintained, with room for repairs and some updates over time.",
        headingTravel: "Travel",
        descriptionTravel: "Some short breaks and domestic trips, including a few nights away each year.",
        headingFood: "Food",
        descriptionFood: "A mix of home cooking and meals out, with occasional take-away or dining at inexpensive restaurants.",
        headingClothes: "Clothes",
        descriptionClothes: "Some spending within a budget, with room to treat yourself now and again."
      },
      comfortable: {
        heading: "Comfortable",
        headingHousehold: "Household expenses",
        descriptionHousehold: "Ongoing home maintenance, updates and improvements, including new appliances over time.",
        headingTravel: "Travel",
        descriptionTravel: "Regular domestic travel, plus overseas trips from time to time.",
        headingFood: "Food",
        descriptionFood: "Regular meals out, takeaway or home delivery, alongside quality food shopping.",
        headingClothes: "Clothes",
        descriptionClothes: "Replacing and updating worn out clothing, with room for occasional splurges."
      }
    },

    disclaimer: {
      tabLabel: `Disclaimer and Assumptions`,
      content: `
      <h4>Disclaimer</h4>
      <p>This calculator provides an illustration of your estimate retirement income. The totals are calculated based on the data you input and can only be as accurate as the information provided.</p>
      <p>Please remember that the results shown by the modeller are for illustrative purposes only and do not guarantee any future outcome or entitlement.</p>
      <p>You should also remember that you will need to pay income tax and the retirement target figure is based on your expenditure, not your pre-tax income.</p>
      <p>Any choices you make or data that you enter while using the calculator are erased once you have finished using the calculator. However, you have a responsibility to ensure that you exit the website when you have finished with the calculator, especially if you leave your computer for any length of time.</p>
      <p>This tool provides estimates only and doesn’t take into account your full financial situation. The results are a guide to help you think about retirement and shouldn’t be relied on as personal advice.</p>
      <p>Consider whether this information is appropriate for you and read the relevant product disclosure statements at csc.gov.au before making decisions.</p>
      <h4>Assumptions</h4>
      <p>Your estimate retirement income calculation is based on research conducted by the Association of Superannuation Funds of Australia (ASFA). The research sets out three ‘Retirement Living Standards’ (Minimum, Moderate and Comfortable) which are based upon a basket of goods and services. You can find out more about the research and each of the Standards at <a href='https://www.superannuation.asn.au/resources/retirement-standard' target='_blank'>https://www.superannuation.asn.au/resources/retirement-standard</a></p>
      <p>Your estimated retirement income figure assumes you will have no rent or mortgage costs to pay in retirement.</p>
      <p>Your monthly savings figure is based on the following assumptions:</p>
      <ul>
        <li>If you selected that you live with a partner, this is your total ‘household’ savings amount.</li>
        <li>It will provide you with an estimated retirement income based on <a href='https://www.superannuation.asn.au/resources/retirement-standard' target='_blank'>ASFA’s Retirement Standards</a> Detailed Expenditure Breakdowns. The figures in each case assume that the retiree(s) own their own home and relate to expenditure by the household. This can be greater than household income after income tax where there is a drawdown on capital over the period of retirement.</li>
        <li>You (and your partner if applicable) will be entitled to the full age pension. For the purposes of the calculation, the calculator assumes your age pension will come into payment from your selected retirement age. To see when you’ll be eligible to receive the age pension visit: <a href='https://www.servicesaustralia.gov.au/who-can-get-age-pension?context=22526' target='_blank'>www.servicesaustralia.gov.au/who-can-get-age-pension</a>.</li>
        <li>All figures and current tax rates are in today’s terms to take into account the effect of inflation.</li>
      </ul>
      `,
    },

    assumptions: {
      content: ``,
    },

    gtag: {
      clickCTA1Description: `Lifestyle retirement`,
      clickCTA2Description: `Get help`,
      clickCTA3Description: `ASFA Standards`
    }
  },

  params: {
    DefaultInvestmentOption: 3,
    DefaultInvestmentOptionInRetirement: 3,

    frequency: {
      year: 1,
      month: 12,
      fortnight: 26.09, // AS Supplied
      week: 52.18, // AS Supplied
      default: 1,
    },

    ageNow: {
      min: 18,
      max: 74,
    },
    salary: {
      min: 100,
      max: 500000,
    },
    superannuationBalance: {
      min: 0,
      max: 3000000,
    },
    employerContributionRate: {
      min: 0,
      max: 20,
    },

    contributionsBeforeTax: {
      min: 0,
      max: 27500,
    },
    contributionsAfterTax: {
      min: 0,
      max: 20000,
    },
    spareCash: {
      min: 0,
      max: 100000,
    },


    retirementAge: {
      min: 55,
      max: 74,
    },
    priceInflation: {
      min: 0,
      max: 10,
    },
    wageInflation: {
      min: 0,
      max: 10,
    },
    insurancePremium: {
      min: 0,
      max: 500,
    },
    feePercentageOnBalance: {
      min: 0,
      max: 3,
    },
    feePercentageOnBalanceCapped: {
      min: 0,
      max: 3,
    },
    feePercentageOnBalanceCappedCap: {
      min: 0,
      max: 5000,
    },
    feePercentageOnContributions: {
      min: 0,
      max: 3,
    },
    feeAnnualDollar: {
      min: 0,
      max: 500,
    },

  },
};
